import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Stack } from '@prenuvo/halo';
import { useShallow } from 'zustand/react/shallow';

import { api } from '@/api';
import { AddendumProvider } from '@/core/addendum';
import { useIsDDFeatureFlagEnabled } from '@/core/data-dog';
import { ML_SERVER } from '@/shared/constant';
import { useStudyGuard } from '@/shared/guard';
import { ReportLinkKey } from '@/shared/model';
import { Loading } from '@/shared/ui';
import { generateAppRoute } from '@/shared/util';
import { useComparisonStore, useReportOrganStore, useReportValidationStore } from '@/store';

import { Header, StudyModeDialog } from '../../component';
import {
  useInitializePreferences,
  useInitializeReport,
  useSyncComparisonStoreWithRoute,
} from '../../hook';
import { LazyComparisonSidePanel } from './comparison-side-panel';
import { KeyImageState } from './key-image-state.component';
import { NavMenu, NavMenuProvider } from './nav-menu';
import { ReportingState } from './reporting-state.component';
import { useRouteValidation } from './use-route-validation.hook';
import { getSelectedLink } from './util';

export function ReportingLayout() {
  const [selectedLink, setSelectedLink] = useState<string>('');
  const [hasComparisonOpened, setHasComparisonOpened] = useState(false);

  const { organ } = useParams<{ organ: string }>();
  const location = useLocation();
  const isValidRoute = useRouteValidation();
  const isComparisonEnabled = useIsDDFeatureFlagEnabled('FCT_COMPARISON_V2');
  const isQuestionnaireResponseEnabled = useIsDDFeatureFlagEnabled('FCT_FHIR_QUESTIONNAIRE_RESPONSE');
  const isRqReportOrganEnabled = useIsDDFeatureFlagEnabled('FCT_RQ_REPORT_ORGAN');

  const { isOpen: isComparisonOpen } = useComparisonStore(
    useShallow((state) => ({ isOpen: state.isOpen })),
  );

  const {
    radiologistScan: { patientID },
    studyId,
  } = useStudyGuard();

  useSyncComparisonStoreWithRoute({ studyId });

  const setReportOrgans = useReportOrganStore((state) => state.setReportOrgans);
  const { batchSetStatuses, getStatus } = useReportValidationStore(
    useShallow((state) => ({
      batchSetStatuses: state.batchSetStatuses,
      getStatus: state.getStatus,
    })),
  );

  const { isLoading: isPreferencesInitLoading } = useInitializePreferences();
  const { isLoading: isReportLoading, report } = useInitializeReport();

  const { isLoading: isReportFindingByReportLoading } =
    api.fct.reportFinding.query.useGetReportFindingsByReport({
      payload: {
        params: {
          size: 50,
          user_id: ML_SERVER,
        },
        uuid: report?.uuid,
      },
    });

  const { isLoading: isHiRadiologistMedicalHistoryLoading } =
    api.hi.radiologist.query.useGetMedicalHistory(
      { payload: { studyId } },
      { enabled: !isQuestionnaireResponseEnabled },
    );

  const { isLoading: isHiStudyMedicalHistoryLoading } = api.hi.study.query.useGetMedicalHistory(
    { payload: { studyId } },
    { enabled: !isQuestionnaireResponseEnabled },
  );

  const { isLoading: isQrRadiologistMedicalHistoryLoading } =
    api.fct.questionnaireResponse.query.useGetRadiologistMedicalHistory(
      { payload: { studyId } },
      { enabled: isQuestionnaireResponseEnabled },
    );

  const { isLoading: isQrStudyMedicalHistoryLoading } =
    api.fct.questionnaireResponse.query.useGetMedicalHistory(
      { payload: { studyId } },
      { enabled: isQuestionnaireResponseEnabled },
    );

  const { isLoading: isAppsStudiesLoading } = api.apps.study.query.useGetStudies(
    { payload: { params: { status: 'scanned', users: [patientID] } } },
    { enabled: !isComparisonEnabled },
  );

  const { isLoading: isPriorStudiesLoading } = api.fct.comparison.query.useGetPriorStudies(
    {
      payload: {
        params: {
          currentStudyId: studyId,
          patientId: patientID,
        },
      },
    },
    { enabled: isComparisonEnabled },
  );

  const { data: reportOrgans, isLoading: isFctReportOrgansLoading } =
    api.fct.reportOrgan.query.useGetReportOrgans(
      {
        payload: {
          reportUuid: report?.uuid || '',
        },
      },
      {
        enabled: Boolean(report?.uuid),
      },
    );

  const isStudiesLoading = isComparisonEnabled ? isPriorStudiesLoading : isAppsStudiesLoading;

  const isMedicalHistoryLoading = isQuestionnaireResponseEnabled
    ? isQrRadiologistMedicalHistoryLoading || isQrStudyMedicalHistoryLoading
    : isHiRadiologistMedicalHistoryLoading || isHiStudyMedicalHistoryLoading;

  const isLoading =
    isPreferencesInitLoading ||
    isReportLoading ||
    isReportFindingByReportLoading ||
    isMedicalHistoryLoading ||
    isStudiesLoading ||
    isFctReportOrgansLoading;

  const handleLinkSelect = useCallback((link: string) => {
    setSelectedLink(link);
  }, []);

  useEffect(() => {
    setSelectedLink(getSelectedLink(location, organ));
  }, [organ, location]);

  useEffect(() => {
    if (reportOrgans && report?.uuid) {
      if (!isRqReportOrganEnabled) {
        setReportOrgans(report.uuid, reportOrgans);
      }

      const statusUpdates = Object.fromEntries(
        reportOrgans.map(({ organ: organName, status }) => {
          const organStatus = getStatus(organName as ReportLinkKey);

          return [
            organName,
            {
              done: status === 'published',
              error: organStatus?.error ?? false,
              warning: organStatus?.warning ?? false,
            },
          ];
        }),
      );

      batchSetStatuses(statusUpdates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportOrgans, report?.uuid]);

  // Eliminate one-render-delay for comparison panel see https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (isComparisonOpen && !hasComparisonOpened) {
    setHasComparisonOpened(true);
  }

  if (isValidRoute) {
    return (
      <AddendumProvider>
        <Stack className="h-screen" gap={0} variant="vertical">
          <Header />
          <main className="flex h-[calc(100%-5rem)] min-h-0 overflow-hidden p-4 pr-0 pb-0">
            <NavMenuProvider onLinkSelect={handleLinkSelect} selectedLink={selectedLink}>
              <NavMenu />
            </NavMenuProvider>

            <Stack className="@container min-h-0 min-w-0 flex-1">
              <Stack className="relative min-w-0 flex-1 overflow-hidden" variant="vertical">
                <Stack className="reporting-content-scroll-area min-h-0 flex-1" variant="vertical">
                  {isLoading ? (
                    <Loading />
                  ) : (
                    <KeyImageState>
                      <ReportingState />
                    </KeyImageState>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </main>

          {isComparisonEnabled && hasComparisonOpened ? <LazyComparisonSidePanel /> : null}

          <StudyModeDialog />
        </Stack>
      </AddendumProvider>
    );
  }

  return <Navigate to={generateAppRoute('NOT_FOUND')} />;
}
